import React, { useRef, useEffect } from 'react'
import Downshift from 'downshift'
import debounce from 'lodash/debounce'
import { RxCross2 } from 'react-icons/rx'

const SearchAutoComplete = ({
  items = [],
  placeholder = 'Search...',
  onSearch = () => {},
  onSelect = () => {},
  onEditIconClick = () => {},
  classNames = '',
  inputClassNames = '',
  autoFocus = false,
  maxResults = 10,
  formatResult,
  enableSearchIcon = true,
  enableEditIcon = false,
  enableClearIcon = true,
  editIconStyle = '',
  searchIconStyle = '',
  clearIconStyle = '',
  debounceTime = 300, // Add debouncing for search
}) => {
  const inputRef = useRef(null)
  const debouncedSearch = debounce(onSearch, debounceTime)

  // Focus input on mount if autoFocus is true
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [autoFocus])

  return (
    <Downshift
      onChange={(selectedItem, { clearSelection }) => {
        if (selectedItem) {
          onSelect(selectedItem)
          clearSelection()
        }
      }}
      itemToString={(item) => (item ? item.name : '')}
      id="downshift-search"
    >
      {({ getInputProps, getItemProps, getMenuProps, inputValue, highlightedIndex, isOpen, clearSelection }) => (
        <div className={`relative ${classNames}`}>
          <div className="relative">
            <input
              {...getInputProps({
                ref: inputRef,
                placeholder,
                autoFocus,
                onChange: (e) => {
                  const value = e.target.value
                  debouncedSearch(value)
                },
              })}
              className={`
                bg-[#F2F2F2] 
                rounded-md 
                text-[13px] 
                w-full 
                p-2 
                pl-3 
                h-[44px] 
                text-center
                placeholder:text-[#3f3f3f]
                placeholder:text-center
                ${enableSearchIcon && enableClearIcon ? 'pr-20' : 'pr-10'}
                ${inputClassNames}
                focus:outline-none 
                focus:ring-1
                focus:ring-custom-voilet
              `}
            />

            {/* Clear Icon */}
            {enableClearIcon && inputValue?.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  clearSelection()
                  onSearch('')
                  if (inputRef.current) inputRef.current.focus()
                }}
                className={`absolute right-10 top-1/2 -translate-y-1/2 cursor-pointer ${clearIconStyle}`}
              >
                <RxCross2 className="w-5 h-5" />
              </button>
            )}

            {/* Edit Icon */}
            {enableEditIcon && (
              <img
                src="/assets/common/pen.svg"
                className={`absolute right-10 cursor-pointer top-[14px] ${editIconStyle}`}
                width="16"
                height="16"
                onClick={onEditIconClick}
                alt="edit"
              />
            )}

            {/* Search Icon */}
            {enableSearchIcon && (
              <img
                src="/assets/common/search_icon.svg"
                className={`absolute right-2 top-[9px] ${searchIconStyle}`}
                width="24"
                height="24"
                alt="search"
              />
            )}
          </div>

          {/* Dropdown Menu */}
          {items?.length > 0 && (
            <ul
              {...getMenuProps()}
              className={`
                list-none 
                mt-1
                p-0 
                absolute 
                z-10 
                w-full 
                max-h-[500px] 
                overflow-y-auto 
                bg-[#F2F2F2] 

                rounded-md 
                shadow-lg
                ${!isOpen && 'hidden'}
              `}
            >
              {isOpen &&
                (inputValue?.length > 0 ? (
                  items
                    .filter((item) => item.name.toLowerCase().includes(inputValue.toLowerCase()))
                    .slice(0, maxResults)
                    .map((item, index) => (
                      <li
                        key={item.alias || `item-${index}`}
                        {...getItemProps({
                          key: item.alias || `item-${index}`,
                          index,
                          item,
                        })}
                        className={`
                          px-3 
                          py-2 
                          cursor-pointer 
                          ${highlightedIndex === index ? 'bg-[#dbdbdb]' : 'bg-[#F2F2F2]'}
                          hover:bg-[#dbdbdb]
                          transition-colors
                        `}
                      >
                        {formatResult ? formatResult(item) : item.name}
                      </li>
                    ))
                ) : (
                  <li className="px-3 py-2 text-sm text-gray-500">Start typing to search...</li>
                ))}
              {isOpen &&
                inputValue?.length > 0 &&
                items.length > 0 &&
                !items.some((item) => item.name.toLowerCase().includes(inputValue.toLowerCase())) && (
                  <li className="px-3 py-2 text-sm text-gray-500">No results found</li>
                )}
            </ul>
          )}
        </div>
      )}
    </Downshift>
  )
}

export default SearchAutoComplete
